import {
    AdvancedMarker,
    InfoWindow,
    Map,
    MapCameraChangedEvent,
    useAdvancedMarkerRef,
    useMap,
} from '@vis.gl/react-google-maps';
import { AutoSearchCheckbox } from './AutoSearchCheckbox';
import { CatalogMapPoint } from '@/redux/modules/auctionsNearMe/catalogsNearMe.types';
import { InfoWindowContent } from './InfoWindowContent';
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

export type MapBounds = {
    northEast: {
        lat: number;
        lng: number;
    };
    southWest: {
        lat: number;
        lng: number;
    };
};

type Props = {
    bounds: MapBounds;
    isGeocoding: boolean;
    mapPoints: CatalogMapPoint[];
    onBoundsChange: (newBounds: MapBounds) => void;
    onLoad?: () => void;
    onUserInteraction?: () => void;
};

export const AuctionsNearMeGoogleMap = ({
    bounds,
    isGeocoding,
    mapPoints,
    onBoundsChange,
    onLoad,
    onUserInteraction,
}: Props): ReactElement => {
    const map = useMap();
    const [selectedPoint, setSelectedPoint] = useState<CatalogMapPoint | null>(null);
    const [selectedMarkerRef, selectedMarker] = useAdvancedMarkerRef();
    const [autoSearch, setAutoSearch] = useState(true);
    const prevBoundsRef = useRef(bounds);
    const isProgrammaticChangeRef = useRef(false);
    const programmaticChangeTimeoutRef = useRef(null);

    // Notify parent when Google Maps is loaded and ready
    // Mark initial camera settling as programmatic so it doesn't trigger a bounds-change fetch
    useEffect(() => {
        if (map && onLoad) {
            onLoad();

            isProgrammaticChangeRef.current = true;
            if (programmaticChangeTimeoutRef.current) {
                clearTimeout(programmaticChangeTimeoutRef.current);
            }
            programmaticChangeTimeoutRef.current = setTimeout(() => {
                isProgrammaticChangeRef.current = false;
                programmaticChangeTimeoutRef.current = null;
            }, 1500);
        }
    }, [map, onLoad]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (programmaticChangeTimeoutRef.current) {
                clearTimeout(programmaticChangeTimeoutRef.current);
            }
        };
    }, []);

    // Convert our MapBounds to google.maps.LatLngBoundsLiteral for the Map component
    const defaultBounds = {
        east: bounds.northEast.lng,
        north: bounds.northEast.lat,
        south: bounds.southWest.lat,
        west: bounds.southWest.lng,
    };

    // When bounds prop changes (e.g., from search), update the map view
    useEffect(() => {
        if (!map) {
            return;
        }

        // Only update if bounds actually changed (not just re-render)
        const boundsChanged =
            prevBoundsRef.current.northEast.lat !== bounds.northEast.lat ||
            prevBoundsRef.current.northEast.lng !== bounds.northEast.lng ||
            prevBoundsRef.current.southWest.lat !== bounds.southWest.lat ||
            prevBoundsRef.current.southWest.lng !== bounds.southWest.lng;

        if (!boundsChanged) {
            return;
        }

        prevBoundsRef.current = bounds;

        // Use Google Maps API to fit the map to the new bounds
        const googleBounds = new google.maps.LatLngBounds(
            { lat: bounds.southWest.lat, lng: bounds.southWest.lng },
            { lat: bounds.northEast.lat, lng: bounds.northEast.lng }
        );

        // Mark this as a programmatic change so handleCameraChanged doesn't trigger a search
        // Clear any existing timeout
        if (programmaticChangeTimeoutRef.current) {
            clearTimeout(programmaticChangeTimeoutRef.current);
        }

        isProgrammaticChangeRef.current = true;
        map.fitBounds(googleBounds);

        // Reset flag after animation completes — must exceed debounce (800ms) to prevent
        // fitBounds camera events from triggering a fetch
        programmaticChangeTimeoutRef.current = setTimeout(() => {
            isProgrammaticChangeRef.current = false;
            programmaticChangeTimeoutRef.current = null;
        }, 1500);
    }, [bounds, map]);

    const handleMarkerClick = useCallback((point: CatalogMapPoint) => {
        // Prevent map camera changes from triggering a new search when clicking a marker
        if (programmaticChangeTimeoutRef.current) {
            clearTimeout(programmaticChangeTimeoutRef.current);
        }

        isProgrammaticChangeRef.current = true;
        setSelectedPoint(point);

        // Reset flag after a short delay (marker clicks don't animate like fitBounds)
        programmaticChangeTimeoutRef.current = setTimeout(() => {
            isProgrammaticChangeRef.current = false;
            programmaticChangeTimeoutRef.current = null;
        }, 300);
    }, []);

    // Debounce the onBoundsChange callback to let zoom/drag animations fully settle and preventing excessive fetch calls
    const debouncedOnBoundsChange = useDebouncedCallback((newBounds: MapBounds) => {
        onBoundsChange(newBounds);
    }, 800);

    const handleCameraChanged = useCallback(
        (event: MapCameraChangedEvent) => {
            // Skip if we're currently geocoding (initial page load with custom search)
            if (isGeocoding) {
                return;
            }

            // Skip if this was a programmatic change (e.g., from fitBounds)
            // Flag is reset by timeout after animation completes
            if (isProgrammaticChangeRef.current) {
                return;
            }

            // Notify parent that the user has manually interacted with the map
            onUserInteraction?.();

            // Only trigger search if "Search as I move the map" is enabled
            if (!autoSearch) {
                return;
            }

            const eventBounds = event.detail.bounds;
            const newBounds: MapBounds = {
                northEast: {
                    lat: eventBounds.north,
                    lng: eventBounds.east,
                },
                southWest: {
                    lat: eventBounds.south,
                    lng: eventBounds.west,
                },
            };

            debouncedOnBoundsChange(newBounds);
        },
        [debouncedOnBoundsChange, autoSearch, isGeocoding, onUserInteraction]
    );

    const handleInfoWindowClose = useCallback(() => {
        setSelectedPoint(null);
    }, []);

    // Group map points by location to show count if multiple catalogs at same location
    const pointsByLocation = useMemo(
        () =>
            mapPoints.reduce(
                (grouped, point) => {
                    const key = `${point.lat},${point.lng}`;
                    if (!grouped[key]) {
                        grouped[key] = [];
                    }
                    grouped[key].push(point);
                    return grouped;
                },
                {} as Record<string, CatalogMapPoint[]>
            ),
        [mapPoints]
    );

    return (
        <div className="flex flex-col gap-xs">
            <div className="h-[500px] w-full">
                <Map
                    defaultBounds={defaultBounds}
                    fullscreenControl={false}
                    gestureHandling="greedy"
                    mapId="auctions-near-me-map"
                    mapTypeControl={false}
                    onCameraChanged={handleCameraChanged}
                >
                    {Object.entries(pointsByLocation).map(([locationKey, pointsAtLocation]) => {
                        const point = pointsAtLocation[0];
                        const isSelected = selectedPoint?.catalogId === point.catalogId;

                        return (
                            <AdvancedMarker
                                key={locationKey}
                                onClick={() => handleMarkerClick(point)}
                                position={{ lat: point.lat, lng: point.lng }}
                                ref={isSelected ? selectedMarkerRef : undefined}
                            />
                        );
                    })}

                    {selectedPoint && selectedMarker && (
                        <InfoWindow
                            anchor={selectedMarker}
                            onClose={handleInfoWindowClose}
                        >
                            <InfoWindowContent
                                catalogId={selectedPoint.catalogId}
                                pointsByLocation={pointsByLocation}
                                selectedPoint={selectedPoint}
                            />
                        </InfoWindow>
                    )}
                </Map>
            </div>
            <AutoSearchCheckbox
                checked={autoSearch}
                onChange={(checked) => setAutoSearch(checked)}
            />
        </div>
    );
};
